import { type FC } from 'react';
import { Card } from '../Card';
import { ExpandableSection } from './components/ExpandableSection';
import { CompletedTrainingCard } from './components/CompletedTrainingCard';
import styled from 'styled-components';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import { useState } from 'react';
import { LabelledToggle } from '../Toggle';
import type { LabelledToggleOption } from '../Toggle/LabelledToggle/LabelledToggle';
import { useManagerTeamCampaignsQuery, type ManagerTeamCampaign } from '../../apiHooks/useManagerTeamCampaignsQuery';
import { ManagerTeamTrainingCard } from './components/ManagerTeamTrainingCard';
import { RTLFixSpan } from './utils/RTLFixSpan';

export const ManagerTeamTrainingWidget: FC = () => {
  const { data: managerListTrainingCampaigns } = useManagerTeamCampaignsQuery();

  const { mandatoryCampaigns, completedCampaigns } = (managerListTrainingCampaigns || []).reduce(
    (
      acc: { mandatoryCampaigns: ManagerTeamCampaign[]; completedCampaigns: ManagerTeamCampaign[] },
      campaign: ManagerTeamCampaign,
    ) => {
      if (campaign.campaignStatus === 'completed') {
        acc.completedCampaigns.push(campaign);
      } else {
        acc.mandatoryCampaigns.push(campaign);
      }
      return acc;
    },
    { mandatoryCampaigns: [], completedCampaigns: [] },
  );
  const toggleLabels: LabelledToggleOption[] = [
    {
      label: <NamedContentEntryText container="span" refKey="yourTraining" subKey="mandatory" />,
      value: 'mandatory',
      selected: true,
    },
    {
      label: <NamedContentEntryText container="span" refKey="yourTraining" subKey="completed" />,
      value: 'completed',
    },
  ];

  const [selectedTrainingType, setSelectedTrainingType] = useState(toggleLabels[0]?.value);
  const onFilterChange = (index: number) => {
    setSelectedTrainingType(toggleLabels[index]?.value);
  };

  return (
    <Card
      title={<NamedContentEntryText container={'span'} refKey="managerSectionTitles" subKey="cybersecurityTraining" />}
    >
      <NamedContentEntryText container={'span'} refKey="managerTeamCyberSecurityTraining" subKey="description" />
      <FiltersBar>
        <LabelledToggle
          fullWidth
          labels={toggleLabels}
          onSelect={(selectedIndex: number) => onFilterChange(selectedIndex)}
        />
      </FiltersBar>
      <ManagerTeamTrainingsContainer>
        {mandatoryCampaigns.map((campaign: ManagerTeamCampaign) => (
          <ManagerTeamTrainingCard
            key={campaign.campaignId}
            title={campaign.campaignName}
            learnersCompletedTotal={campaign.completed || 0}
            totalLearners={campaign.enrolled || 0}
            dueDate={calculateDueDate(campaign.startDate, campaign.durationDays)}
            estimatedCompletionTime={(campaign.mandatoryModules.length + campaign.dynamicModules.length) * 5}
            nudgeActionText={
              <NamedContentEntryText container="span" refKey="managerTeamCyberSecurityTraining" subKey="nudge" />
            }
            campaignId={campaign.campaignId}
          />
        ))}
      </ManagerTeamTrainingsContainer>
      <ExpandableSectionsContainer>
        <ExpandableSection
          title={
            <label>
              <NamedContentEntryText container="span" refKey="yourTraining" subKey="completed" />{' '}
              <RTLFixSpan>({completedCampaigns.length})</RTLFixSpan>
            </label>
          }
          defaultOpen={!!completedCampaigns.length}
        >
          <ExpandableSectionContent>
            {completedCampaigns.map((campaign: ManagerTeamCampaign) => (
              <CompletedTrainingCard
                key={campaign.campaignId}
                campaignUrl={campaign.campaignUrl}
                name={campaign.campaignName}
              />
            ))}
          </ExpandableSectionContent>
        </ExpandableSection>
      </ExpandableSectionsContainer>
      <MobileContainer>
        {selectedTrainingType === 'mandatory' &&
          mandatoryCampaigns.map((campaign: ManagerTeamCampaign) => (
            <ManagerTeamTrainingCard
              key={campaign.campaignId}
              title={campaign.campaignName}
              learnersCompletedTotal={campaign.completed || 0}
              totalLearners={campaign.enrolled || 0}
              dueDate={calculateDueDate(campaign.startDate, campaign.durationDays)}
              estimatedCompletionTime={(campaign.mandatoryModules.length + campaign.dynamicModules.length) * 5}
              nudgeActionText={
                <NamedContentEntryText container="span" refKey="managerTeamCyberSecurityTraining" subKey="nudge" />
              }
              campaignId={campaign.campaignId}
            />
          ))}
        {/*Completed*/}
        {selectedTrainingType === 'completed' &&
          completedCampaigns.map((campaign: ManagerTeamCampaign) => (
            <CompletedTrainingCard
              key={campaign.campaignId}
              campaignUrl={campaign.campaignUrl}
              name={campaign.campaignName}
            />
          ))}
      </MobileContainer>
    </Card>
  );
};

const calculateDueDate = (startDate: string, durationDays: number): string => {
  const start = new Date(startDate);
  start.setDate(start.getDate() + durationDays);
  return start.toISOString();
};

const ExpandableSectionsContainer = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const ExpandableSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 10px;
`;

const FiltersBar = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const ManagerTeamTrainingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media (max-width: 768px) {
    display: none;
  }
`;
